'use client';

/**
 * Wellness Page
 *
 * Provides access to wellness programs including EAP with anonymous mode support.
 * Sensitive content is protected with privacy-first design.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Heart,
  Brain,
  Phone,
  MessageSquare,
  Video,
  Calendar,
  Shield,
  Lock,
  Eye,
  EyeOff,
  ExternalLink,
  ArrowRight,
  CheckCircle,
  Star,
  Users,
  Activity,
  Apple,
  Dumbbell,
  Moon,
  Coffee,
  Zap,
} from 'lucide-react';
import EmployerLayout from '../layout';

interface WellnessProgram {
  id: string;
  name: string;
  icon: React.ElementType;
  description: string;
  category: 'mental' | 'physical' | 'financial' | 'social';
  isConfidential: boolean;
  features: string[];
}

const wellnessPrograms: WellnessProgram[] = [
  {
    id: 'eap',
    name: 'Employee Assistance Program (EAP)',
    icon: Brain,
    description: 'Free, confidential counseling and support for personal and work-life challenges',
    category: 'mental',
    isConfidential: true,
    features: [
      '6 free counseling sessions per issue',
      '24/7 crisis support hotline',
      'Legal and financial consultations',
      'Work-life balance resources',
      'Substance abuse support',
    ],
  },
  {
    id: 'mental-health',
    name: 'Mental Health Resources',
    icon: Heart,
    description: 'Access to mental health apps, therapy, and stress management tools',
    category: 'mental',
    isConfidential: true,
    features: [
      'Headspace/Calm premium access',
      'Virtual therapy sessions',
      'Stress management workshops',
      'Mindfulness programs',
    ],
  },
  {
    id: 'fitness',
    name: 'Fitness & Gym Benefits',
    icon: Dumbbell,
    description: 'Gym memberships, fitness classes, and wellness incentives',
    category: 'physical',
    isConfidential: false,
    features: [
      '$50/month gym stipend',
      'On-site fitness center',
      'Virtual fitness classes',
      'Step challenges with rewards',
    ],
  },
  {
    id: 'nutrition',
    name: 'Nutrition Programs',
    icon: Apple,
    description: 'Nutrition counseling, healthy meal programs, and wellness coaching',
    category: 'physical',
    isConfidential: false,
    features: [
      'Free nutrition consultations',
      'Healthy snacks in office',
      'Weight management programs',
      'Cooking classes',
    ],
  },
  {
    id: 'sleep',
    name: 'Sleep & Recovery',
    icon: Moon,
    description: 'Resources to improve sleep quality and recovery',
    category: 'physical',
    isConfidential: false,
    features: [
      'Sleep tracking programs',
      'Sleep hygiene workshops',
      'Ergonomic assessments',
    ],
  },
  {
    id: 'social',
    name: 'Social Connection',
    icon: Users,
    description: 'Team building, social events, and community involvement',
    category: 'social',
    isConfidential: false,
    features: [
      'Employee resource groups',
      'Team activities',
      'Volunteer opportunities',
      'Mentorship programs',
    ],
  },
];

const eapServices = [
  { id: 'counseling', name: 'Counseling Services', description: '6 free sessions per issue', icon: MessageSquare },
  { id: 'crisis', name: '24/7 Crisis Line', description: 'Immediate confidential support', icon: Phone },
  { id: 'legal', name: 'Legal Consultation', description: 'Free 30-min consultation', icon: Shield },
  { id: 'financial', name: 'Financial Guidance', description: 'Debt, budgeting, planning', icon: Star },
  { id: 'worklife', name: 'Work-Life Support', description: 'Childcare, eldercare resources', icon: Users },
  { id: 'substance', name: 'Substance Support', description: 'Confidential addiction help', icon: Heart },
];

export default function WellnessPage() {
  const { t } = useTranslation('employer');
  const [anonymousMode, setAnonymousMode] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<string | null>(null);
  const [showEapDetails, setShowEapDetails] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const filteredPrograms = categoryFilter === 'all'
    ? wellnessPrograms
    : wellnessPrograms.filter(p => p.category === categoryFilter);

  return (
    <EmployerLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header with Anonymous Mode */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('wellness.title')}</h1>
            <p className="text-gray-600 mt-1">{t('wellness.subtitle')}</p>
          </div>
          <div className="mt-4 md:mt-0">
            <button
              onClick={() => setAnonymousMode(!anonymousMode)}
              className={`
                inline-flex items-center px-4 py-2 rounded-lg border transition-colors
                ${anonymousMode
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }
              `}
            >
              {anonymousMode ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  {t('wellness.anonymousModeOn')}
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  {t('wellness.enableAnonymousMode')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Anonymous Mode Notice */}
        {anonymousMode && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-900">{t('wellness.anonymousBrowsingActive')}</h3>
                <p className="text-sm text-green-700 mt-1">
                  {t('wellness.anonymousBrowsingDesc')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* EAP Highlight Card */}
        <div className="mb-8 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl text-white overflow-hidden">
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="w-5 h-5" />
                  <span className="text-purple-200 text-sm font-medium">{t('wellness.eapConfidential')}</span>
                </div>
                <h2 className="text-2xl font-bold mb-2">{t('wellness.eap')}</h2>
                <p className="text-purple-100 mb-4">
                  Free, confidential support for you and your household members. Your employer
                  never sees who uses EAP services or what they discuss.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowEapDetails(true)}
                    className="px-4 py-2 bg-white text-purple-700 rounded-lg font-medium hover:bg-purple-50 transition-colors"
                  >
                    {t('wellness.viewAllServices')}
                  </button>
                  <a
                    href="tel:1-800-EAP-HELP"
                    className="px-4 py-2 bg-purple-500/30 text-white rounded-lg font-medium hover:bg-purple-500/40 transition-colors flex items-center gap-2"
                  >
                    <Phone className="w-4 h-4" />
                    1-800-EAP-HELP
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/10 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold">6</p>
                  <p className="text-xs text-purple-200">Free Sessions</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold">24/7</p>
                  <p className="text-xs text-purple-200">Crisis Support</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EAP Services Modal/Section */}
        {showEapDetails && (
          <div className="mb-8 bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-4 bg-purple-50 border-b border-purple-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-purple-900">{t('wellness.eapServices')}</h3>
              </div>
              <button
                onClick={() => setShowEapDetails(false)}
                className="text-purple-400 hover:text-purple-600"
              >
                &times;
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {eapServices.map((service) => {
                  const Icon = service.icon;
                  return (
                    <div
                      key={service.id}
                      className="p-4 border border-gray-200 rounded-lg hover:border-purple-200 hover:bg-purple-50/30 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Icon className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{service.name}</h4>
                          <p className="text-sm text-gray-500 mt-1">{service.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">{t('wellness.howToAccessEap')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-purple-600" />
                    <span>Call 1-800-EAP-HELP</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-purple-600" />
                    <span>Schedule video session</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-purple-600" />
                    <span>Chat with counselor</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div className="mb-6 flex flex-wrap gap-2">
          {[
            { id: 'all', label: t('wellness.categoryAll') },
            { id: 'mental', label: t('wellness.categoryMental') },
            { id: 'physical', label: t('wellness.categoryPhysical') },
            { id: 'social', label: t('wellness.categorySocial') },
          ].map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={`
                px-4 py-2 rounded-full text-sm font-medium transition-colors
                ${categoryFilter === cat.id
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Wellness Programs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {filteredPrograms.map((program) => {
            const Icon = program.icon;
            return (
              <div
                key={program.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`
                      p-2 rounded-lg
                      ${program.category === 'mental' ? 'bg-purple-100' :
                        program.category === 'physical' ? 'bg-green-100' :
                        program.category === 'social' ? 'bg-blue-100' : 'bg-amber-100'}
                    `}>
                      <Icon className={`
                        w-5 h-5
                        ${program.category === 'mental' ? 'text-purple-600' :
                          program.category === 'physical' ? 'text-green-600' :
                          program.category === 'social' ? 'text-blue-600' : 'text-amber-600'}
                      `} />
                    </div>
                    {program.isConfidential && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        {t('wellness.confidentialBadge')}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900">{program.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{program.description}</p>
                  <ul className="mt-4 space-y-1">
                    {program.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-500">
                        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                  <button className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                    {t('wellness.learnMore')} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="#"
            className="p-5 bg-gradient-to-br from-green-50 to-teal-50 border border-green-200 rounded-xl hover:shadow-md transition-shadow"
          >
            <Activity className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="font-semibold text-gray-900">{t('wellness.assessmentTitle')}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('wellness.assessmentDesc')}</p>
          </a>
          <a
            href="#"
            className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl hover:shadow-md transition-shadow"
          >
            <Calendar className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="font-semibold text-gray-900">{t('wellness.coachingTitle')}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('wellness.coachingDesc')}</p>
          </a>
          <a
            href="#"
            className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl hover:shadow-md transition-shadow"
          >
            <Zap className="w-8 h-8 text-amber-600 mb-3" />
            <h3 className="font-semibold text-gray-900">{t('wellness.challengesTitle')}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('wellness.challengesDesc')}</p>
          </a>
        </div>
      </div>
    </EmployerLayout>
  );
}
